import { RelationshipDisplay } from '@/features/patient/common/constants';
import type { MappedPersonAttributes } from '@/features/person/schema/person-attribute-types';
import type { Person } from '@/shared/types';
import type { Observation } from '@/shared/types/observation';

import { CONSULTATION_ANSWER_CONCEPTS, CONSULTATION_CONCEPTS, CONSULTATION_ENCOUNTER } from './constants';
import type {
  ConsultationConceptOption,
  ConsultationConceptOptions,
  ConsultationDetailsFormType,
  ConsultationInformantOption,
  ConsultationInformantSelectOption,
} from './schema';

type GroupMember = NonNullable<Observation['groupMembers']>[number];

type ConceptSetMember = {
  uuid: string;
  display: string;
  answers?: Array<{ uuid: string; display: string }>;
};

type ConceptRef = GroupMember['concept'] | string | undefined;

const getConceptUuid = (concept: ConceptRef): string => {
  if (!concept) return '';
  if (typeof concept === 'string') return concept;
  return concept.uuid ?? '';
};

const getCodedMemberValue = (member: GroupMember | undefined): string | undefined => {
  if (!member?.value) return undefined;
  if (typeof member.value === 'object' && 'uuid' in member.value) {
    return member.value.uuid;
  }
  return undefined;
};

const getTextMemberValue = (member: GroupMember | undefined): string => {
  if (!member?.value || typeof member.value !== 'string') return '';
  return member.value;
};

const getMemberStringValue = (member: GroupMember | undefined): string => {
  if (!member?.value) return '';
  if (typeof member.value === 'string') return member.value;
  if (typeof member.value === 'object' && 'uuid' in member.value) {
    return member.value.uuid;
  }
  return '';
};

export const normalizeInformantUuids = (uuids?: string[]): string[] | undefined => {
  if (!uuids?.length) return undefined;
  const unique = Array.from(new Set(uuids.map(uuid => uuid.trim()).filter(Boolean)));
  return unique.length > 0 ? unique : undefined;
};

const findGroupMember = (members: GroupMember[], conceptUuid: string) =>
  members.find(member => getConceptUuid(member.concept) === conceptUuid && !member.voided);

const findGroupMembers = (members: GroupMember[], conceptUuid: string) =>
  members.filter(member => getConceptUuid(member.concept) === conceptUuid && !member.voided);

export const mapConceptAnswersToOptions = (
  answers: Array<{ uuid: string; display: string }> = [],
): ConsultationConceptOption[] =>
  answers.map(answer => ({
    value: answer.uuid,
    label: answer.display,
  }));

export const buildConsultationConceptOptions = (setMembers: ConceptSetMember[] = []): ConsultationConceptOptions => {
  const findMember = (conceptUuid: string) => setMembers.find(member => member.uuid === conceptUuid);

  return {
    modeOfConsultation: mapConceptAnswersToOptions(findMember(CONSULTATION_CONCEPTS.MODE)?.answers),
    informationProvidedBy: mapConceptAnswersToOptions(
      findMember(CONSULTATION_CONCEPTS.INFORMATION_PROVIDED_BY)?.answers,
    ),
    informationReliability: mapConceptAnswersToOptions(findMember(CONSULTATION_CONCEPTS.RELIABILITY)?.answers),
  };
};

export const findConsultationContextObs = (obs: Observation[] = []) => {
  const contexts = obs.filter(
    item => getConceptUuid(item.concept as ConceptRef) === CONSULTATION_CONCEPTS.CONTEXT && !item.voided,
  );
  if (contexts.length === 0) return undefined;
  if (contexts.length === 1) return contexts[0];

  return contexts.sort((a, b) => new Date(b.obsDatetime).getTime() - new Date(a.obsDatetime).getTime())[0];
};

export const parseConsultationContextFromObs = (obs: Observation[] = []): ConsultationDetailsFormType => {
  const contextObs = findConsultationContextObs(obs);
  const members = (contextObs?.groupMembers ?? []).filter(member => !member.voided);

  const mode = getCodedMemberValue(findGroupMember(members, CONSULTATION_CONCEPTS.MODE));
  const informationProvidedBy = getCodedMemberValue(
    findGroupMember(members, CONSULTATION_CONCEPTS.INFORMATION_PROVIDED_BY),
  );
  const informationReliability = getCodedMemberValue(findGroupMember(members, CONSULTATION_CONCEPTS.RELIABILITY));
  const consultationRemark = getTextMemberValue(findGroupMember(members, CONSULTATION_CONCEPTS.REMARK));
  const consultationInformant = normalizeInformantUuids(
    findGroupMembers(members, CONSULTATION_CONCEPTS.INFORMANT).map(member => getMemberStringValue(member)),
  );

  return {
    modeOfConsultation: mode || undefined,
    informationProvidedBy: informationProvidedBy || undefined,
    informationReliability: informationReliability || undefined,
    consultationRemark: consultationRemark || undefined,
    consultationInformant,
  };
};

export const hasConsultationContextData = (data: ConsultationDetailsFormType) =>
  Boolean(
    data.modeOfConsultation ||
    data.informationProvidedBy ||
    data.informationReliability ||
    data.consultationRemark?.trim() ||
    data.consultationInformant?.length,
  );

type ContextGroupMemberPayload = {
  concept: string;
  value: string | { uuid: string };
};

const hasContextMemberValue = (value: string | { uuid: string } | undefined): value is string | { uuid: string } => {
  if (value === undefined || value === '') return false;
  if (typeof value === 'object') return Boolean(value.uuid);
  return true;
};

const pushActiveContextMember = (
  members: ContextGroupMemberPayload[],
  conceptUuid: string,
  value: string | { uuid: string } | undefined,
) => {
  if (!hasContextMemberValue(value)) return;
  members.push({ concept: conceptUuid, value });
};

export const buildConsultationContextObs = (data: ConsultationDetailsFormType, existingContextObs?: Observation) => {
  const activeMembers: ContextGroupMemberPayload[] = [];

  pushActiveContextMember(
    activeMembers,
    CONSULTATION_CONCEPTS.MODE,
    data.modeOfConsultation ? { uuid: data.modeOfConsultation } : undefined,
  );
  pushActiveContextMember(
    activeMembers,
    CONSULTATION_CONCEPTS.INFORMATION_PROVIDED_BY,
    data.informationProvidedBy ? { uuid: data.informationProvidedBy } : undefined,
  );
  pushActiveContextMember(
    activeMembers,
    CONSULTATION_CONCEPTS.RELIABILITY,
    data.informationReliability ? { uuid: data.informationReliability } : undefined,
  );
  pushActiveContextMember(activeMembers, CONSULTATION_CONCEPTS.REMARK, data.consultationRemark?.trim() || undefined);
  for (const informantUuid of normalizeInformantUuids(data.consultationInformant) ?? []) {
    pushActiveContextMember(activeMembers, CONSULTATION_CONCEPTS.INFORMANT, informantUuid);
  }

  if (activeMembers.length === 0) {
    if (existingContextObs?.uuid) {
      return [{ concept: CONSULTATION_CONCEPTS.CONTEXT, uuid: existingContextObs.uuid, voided: true }];
    }
    return [];
  }

  if (existingContextObs?.uuid) {
    return [
      { concept: CONSULTATION_CONCEPTS.CONTEXT, uuid: existingContextObs.uuid, voided: true },
      { concept: CONSULTATION_CONCEPTS.CONTEXT, groupMembers: activeMembers },
    ];
  }

  return [{ concept: CONSULTATION_CONCEPTS.CONTEXT, groupMembers: activeMembers }];
};

export const getConsultationInformantPreviewLabel = (
  personUuid: string | undefined,
  relationships: Array<{
    relation: string;
    relatedPersonName: string;
    relatedPersonUuid: string;
  }> = [],
): string | undefined => {
  if (!personUuid) return undefined;

  const informantRelationship = relationships.find(
    rel => rel.relatedPersonUuid === personUuid && rel.relation === RelationshipDisplay.INFORMANT,
  );
  if (!informantRelationship) return undefined;

  const familyRelationship = relationships.find(
    rel =>
      rel.relatedPersonUuid === personUuid &&
      rel.relation !== RelationshipDisplay.ATTENDANT &&
      rel.relation !== RelationshipDisplay.INFORMANT,
  );

  const relationshipLabel = familyRelationship?.relation ?? RelationshipDisplay.INFORMANT;
  return `${informantRelationship.relatedPersonName}, ${relationshipLabel}`;
};

export const buildInformantRelationshipOptions = (
  relationships: Array<{
    relation: string;
    relatedPersonName: string;
    relatedPersonUuid: string;
  }> = [],
) => {
  const seen = new Set<string>();

  return relationships
    .filter(rel => {
      if (rel.relation !== RelationshipDisplay.INFORMANT) return false;
      if (!rel.relatedPersonUuid || seen.has(rel.relatedPersonUuid)) return false;
      seen.add(rel.relatedPersonUuid);
      return true;
    })
    .map(rel => ({
      value: rel.relatedPersonUuid,
      label: getConsultationInformantPreviewLabel(rel.relatedPersonUuid, relationships) ?? rel.relatedPersonName,
    }));
};

export const mergeInformantOptions = (
  informantOptions: ConsultationInformantOption[],
  selectedValues: string[] = [],
  selectedLabels: Record<string, string> = {},
) => {
  const missingSelections = selectedValues.filter(
    selectedValue => !informantOptions.some(option => option.value === selectedValue),
  );

  if (missingSelections.length === 0) {
    return informantOptions;
  }

  return [
    ...missingSelections.map(selectedValue => ({
      value: selectedValue,
      label: selectedLabels[selectedValue]?.trim() || 'Selected informant',
    })),
    ...informantOptions,
  ];
};

export const getInformantInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('') || '?';

export const buildPersonInformantOptionMeta = (
  person: Person & MappedPersonAttributes,
  kind: 'person' | 'provider',
): ConsultationInformantSelectOption['meta'] => ({
  initials: getInformantInitials(person.display),
  name: person.display,
  displayInfo: [person.gender, person.age ? `${person.age} Y` : null].filter(Boolean).join(' | ') || undefined,
  phoneNumber: person.phoneNumber ?? null,
  isProvider: kind === 'provider',
});

export const buildRelatedInformantOptionMeta = (label: string): ConsultationInformantSelectOption['meta'] => {
  const [namePart, relationshipPart] = label.split(',').map(part => part.trim());

  return {
    initials: getInformantInitials(namePart || label),
    name: namePart || label,
    displayInfo: relationshipPart || 'Informant',
  };
};

export const toInformantSelectOption = (value: string, label: string): ConsultationInformantSelectOption => ({
  value,
  label,
  meta: buildRelatedInformantOptionMeta(label),
});

export const formatInformantOptionLabel = (
  option: ConsultationInformantSelectOption,
  meta: { context: 'menu' | 'value' },
) => {
  const name = option.meta?.name ?? option.label.split(',')[0]?.trim() ?? option.label;

  if (meta.context === 'value') {
    return name;
  }

  if (option.meta?.displayInfo) {
    return `${name} | ${option.meta.displayInfo}`;
  }

  return name;
};

type InformantSearchProviderResult = {
  person: Person & MappedPersonAttributes;
};

type BuildInformantSelectOptionsParams = {
  effectiveOptions: ConsultationInformantOption[];
  selectedOptions: ConsultationInformantSelectOption[];
  searchTerm: string;
  personSearchResults: Array<Person & MappedPersonAttributes>;
  providerSearchResults?: InformantSearchProviderResult[];
};

export const buildInformantSelectOptions = ({
  effectiveOptions,
  selectedOptions,
  searchTerm,
  personSearchResults,
  providerSearchResults,
}: BuildInformantSelectOptionsParams): ConsultationInformantSelectOption[] => {
  const relatedOptions = effectiveOptions.map(option => toInformantSelectOption(option.value, option.label));
  const trimmedSearchTerm = searchTerm.trim();

  let options: ConsultationInformantSelectOption[];

  if (!trimmedSearchTerm) {
    options = relatedOptions;
  } else {
    const normalizedSearch = trimmedSearchTerm.toLowerCase();
    const filteredRelated = relatedOptions.filter(option => option.label.toLowerCase().includes(normalizedSearch));

    const searchOptions: ConsultationInformantSelectOption[] = [
      ...personSearchResults.map(person => ({
        value: person.uuid,
        label: person.display,
        meta: buildPersonInformantOptionMeta(person, 'person'),
      })),
      ...(providerSearchResults ?? []).map(provider => ({
        value: provider.person.uuid,
        label: provider.person.display,
        meta: buildPersonInformantOptionMeta(provider.person, 'provider'),
      })),
    ].filter(
      option =>
        !relatedOptions.some(related => related.value === option.value) &&
        !filteredRelated.some(related => related.value === option.value),
    );

    options = [...filteredRelated, ...searchOptions];
  }

  const missingSelected = selectedOptions.filter(selected => !options.some(option => option.value === selected.value));

  return missingSelected.length > 0 ? [...missingSelected, ...options] : options;
};

export const buildSelectedInformantOptions = (
  value: string[],
  effectiveOptions: ConsultationInformantOption[],
  selectedLabels: Record<string, string>,
): ConsultationInformantSelectOption[] =>
  value.map(uuid => {
    const label = effectiveOptions.find(option => option.value === uuid)?.label ?? selectedLabels[uuid] ?? uuid;
    return toInformantSelectOption(uuid, label);
  });

export const getConsultationInformantsPreviewLabel = (
  personUuids: string[] | undefined,
  relationships: Array<{
    relation: string;
    relatedPersonName: string;
    relatedPersonUuid: string;
  }> = [],
): string | undefined => {
  if (!personUuids?.length) return undefined;

  const labels = personUuids
    .map(uuid => getConsultationInformantPreviewLabel(uuid, relationships))
    .filter((label): label is string => Boolean(label));

  return labels.length > 0 ? labels.join('; ') : undefined;
};

export const getRegisteredInformantPreviewLabel = (
  relationships: Array<{
    relation: string;
    relatedPersonName: string;
    relatedPersonUuid: string;
  }> = [],
): string | undefined => {
  const informantRelationship = relationships.find(r => r.relation === RelationshipDisplay.INFORMANT);
  if (!informantRelationship) return undefined;

  const familyRelationship = relationships.find(
    r =>
      r.relatedPersonUuid === informantRelationship.relatedPersonUuid &&
      r.relation !== RelationshipDisplay.ATTENDANT &&
      r.relation !== RelationshipDisplay.INFORMANT,
  );

  const relationshipLabel = familyRelationship?.relation ?? RelationshipDisplay.INFORMANT;
  return `${informantRelationship.relatedPersonName}, ${relationshipLabel}`;
};

export const belongsToActiveVisit = (
  encounterVisitUuid: string | undefined,
  activeVisitUuid: string | undefined,
): boolean => {
  if (!activeVisitUuid) return false;
  // ponytail: when visit is absent on encounter payload, trust encounterId from active visit list
  if (!encounterVisitUuid) return true;
  return encounterVisitUuid === activeVisitUuid;
};

type ConsultationEncounterRef = {
  uuid?: string;
  display?: string;
  encounterDatetime?: string;
  voided?: boolean;
  encounterType?: { uuid?: string };
  obs?: Observation[];
};

const getMemberDisplayValue = (member: GroupMember | undefined): string => {
  if (!member || member.voided) return '';
  if (typeof member.value === 'object' && member.value && 'display' in member.value) {
    return String(member.value.display ?? '').trim();
  }
  if (typeof member.value === 'string' && member.value.trim()) {
    return member.value.trim();
  }
  if (member.display?.includes(':')) {
    return member.display.split(':').slice(1).join(':').trim();
  }
  return '';
};

export const buildConsultationDetailsTimelineEntries = (
  obs: Observation[] = [],
): Array<{ label: string; value: string }> => {
  const contextObs = findConsultationContextObs(obs);
  const members = (contextObs?.groupMembers ?? []).filter(member => !member.voided);
  if (!members.length) return [];

  const entries: Array<{ label: string; value: string }> = [];
  const pushField = (label: string, concept: string) => {
    const value = getMemberDisplayValue(findGroupMember(members, concept));
    if (value) entries.push({ label, value });
  };

  pushField('Mode of consultation', CONSULTATION_CONCEPTS.MODE);
  pushField('Information provided by', CONSULTATION_CONCEPTS.INFORMATION_PROVIDED_BY);

  const informantLabels = findGroupMembers(members, CONSULTATION_CONCEPTS.INFORMANT)
    .map(member => getMemberDisplayValue(member))
    .filter(Boolean);
  if (informantLabels.length) {
    entries.push({ label: 'Informant(s)', value: informantLabels.join('; ') });
  }

  pushField('Reliability of information', CONSULTATION_CONCEPTS.RELIABILITY);
  pushField('Consultation remark', CONSULTATION_CONCEPTS.REMARK);

  return entries;
};

export const isConsultationDetailsEncounter = (
  encounter: ConsultationEncounterRef,
  encounterTypeUuid?: string,
): boolean => {
  if (encounter.voided) return false;
  if (encounterTypeUuid && encounter.encounterType?.uuid === encounterTypeUuid) return true;
  return Boolean(encounter.display?.includes(CONSULTATION_ENCOUNTER.display));
};

export const pickLatestConsultationEncounter = (
  encounters: ConsultationEncounterRef[] = [],
  encounterTypeUuid?: string,
): ConsultationEncounterRef | undefined => {
  const matches = encounters.filter(encounter => isConsultationDetailsEncounter(encounter, encounterTypeUuid));
  if (matches.length === 0) return undefined;

  return matches.sort(
    (a, b) => new Date(b.encounterDatetime ?? 0).getTime() - new Date(a.encounterDatetime ?? 0).getTime(),
  )[0];
};

export const extractConsultationDetailsTimelineEntries = (
  encounters: ConsultationEncounterRef[] = [],
): Array<{ label: string; value: string }> => {
  const latest = pickLatestConsultationEncounter(encounters, CONSULTATION_ENCOUNTER.uuid);
  if (!latest?.obs?.length) return [];
  return buildConsultationDetailsTimelineEntries(latest.obs);
};

export const getVisitScopedConsultationValues = (
  activeVisitUuid: string | undefined,
  encounterId: string | undefined,
  encounterVisitUuid: string | undefined,
  encounterObs: Observation[] = [],
): ConsultationDetailsFormType => {
  if (!activeVisitUuid || !encounterId) {
    return {};
  }

  if (!belongsToActiveVisit(encounterVisitUuid, activeVisitUuid)) {
    return {};
  }

  return parseConsultationContextFromObs(encounterObs);
};

const CONSULTATION_DEFAULT_LABEL_PATTERNS = {
  modeInPerson: /in[- ]?person/i,
  modeTele: /tele|virtual|video/i,
  reliabilityReliable: /^reliable$/i,
} as const;

export const findConceptOptionValue = (options: ConsultationConceptOption[], pattern: RegExp): string | undefined =>
  options.find(option => pattern.test(option.label.trim()))?.value;

// Virtual appointment → tele consultation; anything else (scheduled, walk-in, none) → in-person.
export const getConsultationModeForAppointmentKind = (
  modeOptions: ConsultationConceptOption[],
  appointmentKind?: string,
): string | undefined =>
  findConceptOptionValue(
    modeOptions,
    appointmentKind === 'Virtual'
      ? CONSULTATION_DEFAULT_LABEL_PATTERNS.modeTele
      : CONSULTATION_DEFAULT_LABEL_PATTERNS.modeInPerson,
  );

export const resolveConsultationFormValues = (
  saved: ConsultationDetailsFormType,
  conceptOptions: ConsultationConceptOptions,
): ConsultationDetailsFormType => {
  if (hasConsultationContextData(saved)) {
    return saved;
  }

  return {
    ...saved,
    modeOfConsultation:
      findConceptOptionValue(conceptOptions.modeOfConsultation, CONSULTATION_DEFAULT_LABEL_PATTERNS.modeInPerson) ??
      saved.modeOfConsultation,
    informationProvidedBy: CONSULTATION_ANSWER_CONCEPTS.INFORMATION_FROM_PATIENT,
    informationReliability:
      findConceptOptionValue(
        conceptOptions.informationReliability,
        CONSULTATION_DEFAULT_LABEL_PATTERNS.reliabilityReliable,
      ) ?? saved.informationReliability,
  };
};

const labelForConceptValue = (options: ConsultationConceptOption[], value: string) =>
  options.find(option => option.value === value)?.label ?? value;

export type ConsultationDetailsPrintRow = {
  label: string;
  value: string | undefined;
  /** When true, the label is shown alongside the value in print (otherwise value only). */
  showLabel?: boolean;
};

export const buildConsultationDetailsPrintRows = (
  data: ConsultationDetailsFormType,
  conceptOptions: ConsultationConceptOptions,
  relationships: Array<{
    relation: string;
    relatedPersonName: string;
    relatedPersonUuid: string;
  }> = [],
): ConsultationDetailsPrintRow[] => {
  const rows: ConsultationDetailsPrintRow[] = [];
  const informantLabel = getConsultationInformantsPreviewLabel(data.consultationInformant, relationships);

  if (data.modeOfConsultation) {
    rows.push({
      label: 'Mode of consultation',
      value: labelForConceptValue(conceptOptions.modeOfConsultation, data.modeOfConsultation),
    });
  }

  if (data.informationProvidedBy) {
    const sourceLabel = labelForConceptValue(conceptOptions.informationProvidedBy, data.informationProvidedBy);
    const informationBy =
      isInformantSource(data.informationProvidedBy) && informantLabel
        ? `${sourceLabel} (${informantLabel})`
        : sourceLabel;

    rows.push({
      label: 'Information provided by',
      value: informationBy,
    });
  }

  if (data.consultationRemark?.trim()) {
    rows.push({
      label: 'Remark',
      value: data.consultationRemark.trim(),
      showLabel: true,
    });
  }

  return rows;
};

export const isInformantSource = (informationProvidedBy: string | undefined) =>
  informationProvidedBy === CONSULTATION_ANSWER_CONCEPTS.INFORMATION_FROM_INFORMANT ||
  informationProvidedBy === CONSULTATION_ANSWER_CONCEPTS.INFORMATION_FROM_BOTH;
